// One source of truth for the OQL semantic-role palette, shared by both OQL
// surfaces so they read as one feature (#357 text editor + #428 no-code builder):
//   keyword     (Find / where / sort by / group by / sample)  slate  — structural, inert
//   conjunction (and / or)                                    yellow — toggleable joins
//   property    (field names, entity, boolean phrases)        violet
//   relation    (is / has / > / is not / similar to)     sky
//   value       (ids, strings, numbers, enum slugs)           teal
// The builder paints bg-filled "bricks"; the editor colors bare text — same hues.
//
// PERCEPTUALLY UNIFORM (iter 18.2): the colours are generated in OKLCH so every
// role carries the same perceptual weight — the old Tailwind -100 steps were NOT
// iso-lightness (bg chroma ran yellow .088 > teal .050 > violet .028 > sky .025).
// Both tiers now share one (lightness, chroma) pair, differing only in hue:
//   backgrounds  oklch(0.94, 0.0306, H)  — C capped by the violet/sky sRGB
//                gamut limit at this lightness (the binding constraint);
//                raised from L=0.925 per Jason ("lighter and airier", 18.3)
//   foregrounds  oklch(0.45, 0.0726, H)  — C capped by the teal gamut limit
// Keyword keeps its hue but only 30% of the tier chroma: it's the structural,
// near-neutral role and shouldn't compete. Hue identity comes from the previous
// palette (bg yellow sits at H≈96 — the amber-500 anchor H≈70 reads tan at low
// chroma; dark-yellow text reads olive, so the conjunction FG keeps the warm
// brown H≈47). Regenerate with the script in the #428 job log (oxjobs) if hues
// ever change. History: amber-100 (iter 11, faint) → pink (iter 17, nope) →
// amber-200 (iter 18, heavy) → OKLCH-uniform (iter 18.2).
export const OQL_ROLES = {
  keyword:     { fg: "#4e5662", bg: "#e7ecf1" },   // slate hue, 30% tier chroma
  conjunction: { fg: "#764831", bg: "#f1ecd5" },   // yellow bg / warm brown fg
  property:    { fg: "#574d7a", bg: "#ece8fe" },   // violet
  relation:    { fg: "#2e5a7a", bg: "#d9effd" },   // sky
  value:       { fg: "#14625c", bg: "#d6f2ec" },   // teal
};

// `[Name]` annotations in the editor: inert decoration, gray (not a role).
export const OQL_ANNOTATION_FG = "#94a3b8"; // slate-400

// The builder's brick CSS vars (consumed by .builder and every row/group/value
// component under it — see OqlQueryBuilder.vue). Bound via :style on the root so
// the stylesheet carries no hex of its own.
//
// COLOR-CODED BUILDER (Jason 2026-06-24, #507): the builder bricks carry one hue per block
// family, drawn from the ColorBrewer **Pastel2** qualitative palette (Jason picked it from a
// 4-way bake-off of already-light palettes). The earlier attempts LIGHTENED a dark palette
// (Dark2) into tints, which still read as red/green/blue; the fix is to use a palette that is
// LIGHT in its primary state — the resting fill IS the native pastel, and the brick only goes
// dark on SELECTION. Mapping (Jason 2026-06-24 — connectors GRAY, only content carries hue):
//   conjunction (and / or / & / the → arrow blocks)   NEUTRAL GRAY (#d7d7d7, weight-matched)
//   property    (field names, with the folded-in predicate `is`/`has`/`≥`)   PEACH (#fdcdac, H≈56)
//   value       (ids, strings, numbers, dates, enums)  MINT       (Pastel2 #b3e2cd, H≈166)
//   keyword     (where / sort by / return)             stays NEUTRAL slate — not one of the
//               three families; it's structural, inert, shouldn't compete.
// The per-role HUES of OQL_ROLES (above) are UNCHANGED — they still color the #357 TEXT editor
// (oqlLanguage.js); this object only repaints the no-code builder bricks.
//
// Each family is one native Pastel2 fill (the resting brick) plus three OKLCH-derived shades
// at the SAME hue: a darker tint for hover, a dark fill + white text for selection, and a dark
// shade for the resting label.
//   resting bg   the native Pastel2 hex (light, the primary state)
//   resting fg   oklch(0.42, gamut-capped, H)  — readable dark label on the pastel
//   hover bg     native − 0.06 L (same hue, slightly darker — NOT a grey wash; fixes the
//                hover-desaturates-to-grey bug)
//   selected     oklch(0.50, ~0.15, H) fill + white text — "keeps its colour, goes dark, text
//                goes light" (Jason).
// Regenerate via /tmp/oklch3.mjs (genNative) if the source hexes change.
//
// resting bg/fg        hover bg     selected bg/fg
//   conn  #d7d7d7 #4d4d4d  #c3c3c3    #4d4d4d #fff   (neutral GRAY)
//   prop  #fdcdac #763b00  #e9ba99    #964d00 #fff   (peach)
//   val   #b3e2cd #005c42  #a0ceba    #007555 #fff   (mint)
// keyword stays neutral slate (bg #e7ecf1 / fg #4e5662) — unchanged, never selected.
//
// CONNECTOR IS GRAY (Jason 2026-06-24, #507): the conjunction family (`&`/`or` cells +
// the `→` arrow cells) is now NEUTRAL gray, not periwinkle — only the two CONTENT families
// (peach property / mint value) carry hue. The gray's resting LIGHTNESS is computed in OKLCH
// to sit at the SAME perceptual weight as the two pastels (L≈0.878, the avg of peach #fdcdac
// L=0.882 and mint #b3e2cd L=0.874) at chroma 0, so the three resting brick fills read as one
// even tonal family. fg label = neutral L≈0.42 (#4d4d4d, matching the colored fgs' lightness);
// hover = resting −0.06 L (#c3c3c3); selected = a darker neutral (#4d4d4d) + white text.
// GRAY/BLUE SIMPLIFICATION (Jason 2026-06-24, #507 change #3). Another pass at calming the
// builder's colour: the only thing that carries hue now is the VALUES (the search terms the user
// actually cares about) — everything structural is neutral gray.
//   field / property                          GRAY  (de-emphasised; was peach)
//   FILTER-level conn (`&`/`or`) + arrow      GRAY  (#d7d7d7, unchanged)
//   VALUE-level  conn (`&`/`or`) + arrow      BLUE + bold  (--vconn-*, the link blue)
//   value (ids, strings, numbers, …)          BLUE  (--ox-link #1f6feb, the exact site link colour)
//   keyword (where / sort by / return)        neutral slate (unchanged)
// Value chips + value-level connectors share the SAME blue identity; the connectors go BOLD so
// they still stand out against the value chips of the same colour (Jason). The `--vconn-*` vars
// drive both the column cells (`.bl-conn-cell.bl--val` / `.bl-arrow.bl--val`) and the inline
// `OqlConnChip` (which is always value-level by construction).
// TWO-FAMILY PALETTE (Jason 2026-06-24 #507): the builder reads as two colour families —
//   FILTER family (peach): field chips + FILTER-level conjunctions / arrows / summary
//   VALUE  family (blue):  value chips + VALUE-level  conjunctions / arrows / summary
// keyword (where / sort by / return) stays neutral slate. The PEACH is generated at the SAME
// OKLCH weight as the value BLUE (bg L≈0.926 C≈0.035, fg L≈0.569 C≈0.202; hue → peach H≈56) so
// the two families carry equal perceptual weight — peach was brought back at the blue's weight
// (Jason: "bring back the peach … match colour weight to the new blue"). No bold anywhere now.
// Regenerate via /tmp/peach.mjs if the blue source changes.
const SEL_FG = "#ffffff";
const LINK_BLUE = "#1f6feb";
export const OQL_ROLE_CSS_VARS = {
  // keyword — neutral, structural (unchanged slate)
  "--kw-fg": "#4e5662",
  "--kw-bg": "#e7ecf1",
  // conjunction (FILTER level) — SAME peach TEXT as the field chips, only a LIGHTER background
  // so the connectors recede without changing the label colour (oxjob #507, Jason 2026-06-25:
  // "same as the other chips... just fade the background color, not the text color").
  // #595 round 2 (Jason 2026-07-10): lightened further (#f9ebe2 → #fdf6f0) so the
  // light = non-interactive vs dark = interactive chip distinction reads more clearly.
  "--conn-fg": "#b25d06",
  "--conn-bg": "#fdf6f0",
  "--conn-bg-hov": "#e8dbd2",
  "--conn-fg-sel": SEL_FG,
  "--conn-bg-sel": "#b25d06",
  // conjunction (VALUE level) — SAME blue TEXT as the value chips, only a LIGHTER background.
  // Lightened with the peach (#e7eefb → #f2f6fd) — same #595 round-2 pass.
  "--vconn-fg": LINK_BLUE,
  "--vconn-bg": "#f2f6fd",
  "--vconn-bg-hov": "#d7deea",
  "--vconn-fg-sel": SEL_FG,
  "--vconn-bg-sel": LINK_BLUE,
  // property / field — PEACH (the filter family)
  "--prop-fg": "#b25d06",
  "--prop-bg": "#fae1d1",
  "--prop-bg-hov": "#f3d0b7",
  "--prop-fg-sel": SEL_FG,
  "--prop-bg-sel": "#b25d06",
  // relation — unused in the builder (predicate folds into the field); mirror prop.
  "--rel-fg": "#b25d06",
  "--rel-bg": "#fae1d1",
  // value — BLUE (link blue): light tint bg + the exact link colour as the label
  "--val-fg": LINK_BLUE,
  "--val-bg": "#dbe7ff",
  "--val-bg-hov": "#c7d8fb",
  "--val-fg-sel": SEL_FG,
  "--val-bg-sel": LINK_BLUE,
};
